/**
 * Integrytas Group - Base App (MOBILE)
 * Page: GTC.TRASH — mobile-native (Global Trash Can & Deep Undo).
 *
 * Touch-first counterpart of src/pages/admin/GtcTrashPage.tsx. A DISTINCT
 * presentation (dense card list + entity facet chips + bottom-sheet detail with a
 * Restore action) over the SAME data/logic layer only:
 *   - reads via apiDb (gtc_ledger where status='TRASHED', plus gtc_managed_entity),
 *   - Restore performs the Deep Undo through the module's own sanctioned RPC
 *     `gtc_restore` — exactly like the desktop page. That RPC is the trash-can's
 *     atomic tree-restore entry point (not a business write), so it does NOT go
 *     through routed_writes; there is NO direct apiDb insert/update/delete here.
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Trash2, RotateCcw, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDate, useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

interface GtcTrashRow {
  gtc_ledger_id: number;
  root_entity: string;
  root_pk_value: string;
  label: string | null;
  affected_count: number;
  deleted_at: string;
  expires_at: string | null;
}

interface ManagedRow {
  entity_name: string;
}

interface RestoreResult {
  ok: boolean;
  restored?: number;
  error_code?: string;
}

const ALL_ENTITIES = '__all__';

// A stable, translated title for one trashed operation — never a raw id: prefer the
// human label captured at delete time, else "<entity> #<pk>".
function itemTitle(r: GtcTrashRow): string {
  return r.label ?? `${r.root_entity} #${r.root_pk_value}`;
}

const PAGE_CODE = 'GTC.TRASH';

export function MobileGtcTrashPage({ session }: MobilePageComponentProps) {
  // §6/§17: releasing another user's lock / restoring or resolving shared
  // state is page-scoped, so the affordance follows csm_role_page_access.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const formatDateTime = useFormatDateTime();

  const [rows, setRows] = useState<GtcTrashRow[]>([]);
  const [managed, setManaged] = useState<ManagedRow[]>([]);
  const [filter, setFilter] = useState('');
  const [entityFacet, setEntityFacet] = useState<string>(ALL_ENTITIES);
  const [selected, setSelected] = useState<GtcTrashRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [ledger, managedEntities] = await Promise.all([
        apiDb
          .from('gtc_ledger')
          .select('gtc_ledger_id, root_entity, root_pk_value, label, affected_count, deleted_at, expires_at')
          .eq('status', 'TRASHED')
          .order('deleted_at', { ascending: false })
          .limit(200),
        apiDb
          .from('gtc_managed_entity')
          .select('entity_name')
          .eq('is_active', 'Y')
          .order('entity_name'),
      ]);
      if (ledger.error) throw ledger.error;
      if (managedEntities.error) throw managedEntities.error;
      setRows((ledger.data ?? []) as GtcTrashRow[]);
      setManaged((managedEntities.data ?? []) as ManagedRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount only; the trash contents don't depend on the active locale.
  useEffect(() => { void reload(); }, []);

  // Distinct entity types present in the trash, driving the facet chips (§17.B5).
  const entityValues = useMemo(
    () => [...new Set(rows.map((r) => r.root_entity).filter(Boolean))].sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (entityFacet !== ALL_ENTITIES && r.root_entity !== entityFacet) return false;
      if (!q) return true;
      return (
        (r.label ?? '').toLowerCase().includes(q) ||
        r.root_entity.toLowerCase().includes(q) ||
        r.root_pk_value.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, entityFacet]);

  const affectedLabel = (n: number) => t('mobile.gtc.records', { count: n, defaultValue: `${n} record(s)` });

  const restore = async (r: GtcTrashRow) => {
    setActionBusy(true);
    setError(null);
    try {
      // Deep Undo via the module's sanctioned RPC (atomic tree restore) — mirrors
      // the desktop page; RLS still applies as the authenticated user through the gateway.
      const { data, error: e } = await apiDb.rpc('gtc_restore', { p_ledger_id: r.gtc_ledger_id });
      if (e) throw e;
      const result = data as RestoreResult | null;
      if (!result?.ok) throw new Error(result?.error_code ?? 'GTC.RESTORE_FAILED');
      setSelected(null);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.gtc.title', 'Trash')}
        description={t('mobile.gtc.description', 'Recently deleted records, restorable as a whole tree.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.gtc.stat.items', 'Items') },
          { value: entityValues.length, label: t('mobile.gtc.stat.types', 'Types') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.gtc.search', 'Search trash')}
          aria-label={t('mobile.gtc.search', 'Search trash')}
        />
      </div>

      <MobileFilterChips
        chips={[
          { value: ALL_ENTITIES, label: t('mobile.gtc.all', 'All') },
          ...entityValues.map((value) => ({ value, label: value })),
        ]}
        isActive={(v) => entityFacet === v}
        onSelect={(v) => setEntityFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.gtc.none', 'Trash is empty')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.gtc_ledger_id}
              lead={<Trash2 />}
              leadTone="amber"
              title={itemTitle(r)}
              subtitle={`${r.root_entity} · ${affectedLabel(r.affected_count)}`}
              trailing={<MobileStatusPill tone="wait">{formatDate(r.deleted_at)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {managed.length > 0 && (
        <MobileContentCard>
          <MobileDetailRow
            label={t('mobile.gtc.managed', 'Trash-managed')}
            value={managed.map((m) => m.entity_name).join(', ')}
          />
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.gtc.detail', 'Trash item detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Trash2 size={20} /></span>
              <span>
                <div className="m-nm">{itemTitle(selected)}</div>
                <div className="m-em">{selected.root_entity} #{selected.root_pk_value}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.gtc.entity', 'Entity')} value={selected.root_entity} />
              <MobileDetailRow label={t('mobile.gtc.affected', 'Affected')} value={affectedLabel(selected.affected_count)} />
              <MobileDetailRow label={t('mobile.gtc.deleted', 'Deleted')} value={formatDateTime(selected.deleted_at)} />
              <MobileDetailRow
                label={t('mobile.gtc.auto_purge', 'Auto-purge')}
                value={selected.expires_at ? formatDate(selected.expires_at) : t('common.none', 'None')}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => void restore(selected)}
              >
                <RotateCcw size={16} />
                {actionBusy ? t('mobile.gtc.restoring', 'Restoring…') : t('mobile.gtc.restore', 'Restore')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
